import * as React from "react"
import { DEFAULT_LOCALE, type SupportedLocale, getPreferredLocale } from "../i18n/locales"
import { isRuntimeOnlyPath, resolveInitialLocale } from "../i18n/routing"

type LanguageContextValue = {
  language: SupportedLocale
  setLanguage: (language: SupportedLocale) => void
}

const LanguageContext = React.createContext<LanguageContextValue>({
  language: DEFAULT_LOCALE,
  setLanguage: () => undefined,
})

type LanguageProviderProps = {
  children: React.ReactNode
  initialLanguage?: string | null
  pathname?: string | null
  search?: string | null
}

export const LanguageProvider: React.FC<LanguageProviderProps> = ({
  children,
  initialLanguage,
  pathname,
  search,
}) => {
  const initial = React.useMemo(
    () => resolveInitialLocale(pathname, search, initialLanguage),
    [initialLanguage, pathname, search]
  )

  const [language, setLanguageState] = React.useState<SupportedLocale>(initial)

  React.useEffect(() => {
    setLanguageState(resolveInitialLocale(pathname, search, initialLanguage))
  }, [initialLanguage, pathname, search])

  React.useEffect(() => {
    if (typeof window === "undefined") return

    if (!isRuntimeOnlyPath(pathname) || search?.includes("lang=")) return

    const stored = window.localStorage.getItem("language")
    if (!stored) return

    const preferred = getPreferredLocale(stored)
    if (preferred !== language) {
      setLanguageState(preferred)
    }
  }, [language, pathname, search])

  React.useEffect(() => {
    if (typeof window === "undefined") return

    window.localStorage.setItem("language", language)
    document.documentElement.lang = language
  }, [language])

  const setLanguage = React.useCallback((nextLanguage: SupportedLocale) => {
    const safeLanguage = getPreferredLocale(nextLanguage)
    setLanguageState(safeLanguage)
  }, [])

  const value = React.useMemo(() => ({ language, setLanguage }), [language, setLanguage])

  return <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>
}

export const useLanguage = () => React.useContext(LanguageContext)
